// Vendor imports
const _ = require('lodash')
// Protobuf imports
const protobufjs = require('protobufjs/minimal')
protobufjs.util.Long = require('long') // Support for 64 bit values
const pb = require('messages/client/protobuf')
const pbPageContextUtils = require('messages/shared/page-context')(pb.globals)
// Client lib imports
const airbrake = require('client/lib/airbrake')
const { send: ajax } = require('client/lib/request')
// Sibling imports
const pageContextUtils = require('./page-context')

/** @typedef { import('bowser').Parser.Parser } BowserParser */
/** @typedef { import('./types.d').PageContext } PageContext */
/** @typedef { import('./types.d').PageContextWithReferrer } PageContextWithReferrer */

/**
 * @typedef {Object} CreateCommandContextOptions
 * @property {BowserParser} browser
 * @property {string} buildNumber
 * @property {number} eventTimestamp
 * @property {PageContextWithReferrer} pageContext
 * @property {string} sessionId
 */

/**
 * @param {CreateCommandContextOptions} options
 */
function createCommandContext(options) {
  const {
    browser,
    buildNumber,
    eventTimestamp,
    pageContext,
    sessionId
  } = options
  const currentPageContext = pageContextUtils.unpackCurrentPageContext(pageContext)
  const referrerPageContext = pageContextUtils.unpackReferrerPageContext(pageContext)
  return pb.commands.CommandContext.fromObject({
    eventTimestamp,
    session: {
      sessionId,
      device: {
        deviceType: getDeviceType(browser),
        userAgent: navigator.userAgent
      },
      appVersion: {
        appType: 'WEBAPP',
        buildNumber
      }
    },
    // Always send `currentContext` because it is a required field
    currentContext: pbPageContextUtils.convertPageContext(currentPageContext),
    // Don't send `referrerContext` if there is no referrer page name
    referrerContext: (
      referrerPageContext['page_name']
      ? pbPageContextUtils.convertPageContext(referrerPageContext)
      : null
    )
  })
}

/**
 * @param {string} commandName
 * @param {any} params
 */
function getCommandJSON(commandName, commandParams) {
  const commandClass = pb.commands[commandName]
  if (!commandClass) {
    throw new Error(`Invalid Protobuf commandName[${commandName}]`)
  }
  const commandInstance = commandClass.fromObject(commandParams)
  const verifyMessage = commandClass.verify(commandInstance)
  if (verifyMessage) {
    const err = new Error(`protobufjs command[${commandName}] verify failed`)
    airbrake.notify({
      error: err,
      params: {
        commandName,
        commandParams,
        verifyMessage
      }
    })
  }
  return commandInstance.toJSON()
}

/**
 * @param {string} commandName
 * @param {any} params
 * @param {CreateCommandContextOptions} options
 */
async function sendCommand(commandName, params, options) {
  const context = createCommandContext(options)
  await ajax({
    url: `/api/v0.1/commands/${_.kebabCase(commandName)}`,
    method: 'post',
    contentType: 'application/json',
    data: getCommandJSON(commandName, { context, ...params }),
    sessionId: options.sessionId,
    includePageContext: false // It's already in the command JSON
  })
}

module.exports = {
  sendCommand
}

/**
 * @param {BowserParser} browser
 * @returns {string}
 */
function getDeviceType(browser) {
  const osName = browser.getOSName()
  const platformType = browser.getPlatformType()
  if (osName === 'iOS') {
    return platformType === 'tablet' ? 'IPAD' : 'IPHONE'
  }
  if (osName === 'Android') {
    return platformType === 'tablet' ? 'ANDROID_TABLET' : 'ANDROID'
  }
  if (platformType === 'desktop') {
    return 'DESKTOP'
  }
}
